import { useState, useEffect, useCallback } from 'react';
import {
  adminApi,
  AdminTemplateSection,
  AdminTemplateSectionCreate,
  AdminTemplateSectionUpdate,
  AdminTemplateItemCreate,
  AdminTemplateItemUpdate,
  AdminTemplateItem,
} from '../../../../services/api/admin';

interface UseAdminCapexTemplateResult {
  sections: AdminTemplateSection[];
  loading: boolean;
  addSection: (data: AdminTemplateSectionCreate) => Promise<AdminTemplateSection>;
  updateSection: (sectionId: string, data: AdminTemplateSectionUpdate) => Promise<AdminTemplateSection>;
  removeSection: (sectionId: string) => Promise<void>;
  addItem: (sectionId: string, data: AdminTemplateItemCreate) => Promise<AdminTemplateItem>;
  updateItem: (sectionId: string, itemId: string, data: AdminTemplateItemUpdate) => Promise<AdminTemplateItem>;
  removeItem: (sectionId: string, itemId: string) => Promise<void>;
}

export function useAdminCapexTemplate(orgId: string): UseAdminCapexTemplateResult {
  const [sections, setSections] = useState<AdminTemplateSection[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const data = await adminApi.getCapexTemplate(orgId);
      setSections(data);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const addSection = useCallback(async (data: AdminTemplateSectionCreate): Promise<AdminTemplateSection> => {
    const section = await adminApi.addCapexSection(orgId, data);
    setSections(prev => [...prev, section]);
    return section;
  }, [orgId]);

  const updateSection = useCallback(async (sectionId: string, data: AdminTemplateSectionUpdate): Promise<AdminTemplateSection> => {
    const updated = await adminApi.updateCapexSection(orgId, sectionId, data);
    setSections(prev => prev.map(s => s.id === sectionId ? { ...updated, items: s.items } : s));
    return updated;
  }, [orgId]);

  const removeSection = useCallback(async (sectionId: string): Promise<void> => {
    await adminApi.deleteCapexSection(orgId, sectionId);
    setSections(prev => prev.filter(s => s.id !== sectionId));
  }, [orgId]);

  const addItem = useCallback(async (sectionId: string, data: AdminTemplateItemCreate): Promise<AdminTemplateItem> => {
    const item = await adminApi.addCapexItem(orgId, sectionId, data);
    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, items: [...s.items, item] } : s
    ));
    return item;
  }, [orgId]);

  const updateItem = useCallback(async (sectionId: string, itemId: string, data: AdminTemplateItemUpdate): Promise<AdminTemplateItem> => {
    const updated = await adminApi.updateCapexItem(orgId, sectionId, itemId, data);
    setSections(prev => prev.map(s =>
      s.id === sectionId
        ? { ...s, items: s.items.map(i => i.id === itemId ? updated : i) }
        : s
    ));
    return updated;
  }, [orgId]);

  const removeItem = useCallback(async (sectionId: string, itemId: string): Promise<void> => {
    await adminApi.deleteCapexItem(orgId, sectionId, itemId);
    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, items: s.items.filter(i => i.id !== itemId) } : s
    ));
  }, [orgId]);

  return { sections, loading, addSection, updateSection, removeSection, addItem, updateItem, removeItem };
}
